import './contragents.css';
import '../dialogs/dialogs.css';
import {
    Button, Checkbox, Form, Input, Modal, Popconfirm, Segmented, Select, Tabs, Tag, Tooltip, Upload, message,
} from 'antd';
import {
    BankOutlined, CalendarOutlined, ClockCircleOutlined, CopyOutlined, DeleteOutlined, EnvironmentOutlined,
    FileTextOutlined, IdcardOutlined,
    MailOutlined, PhoneOutlined, PlusOutlined, ShoppingCartOutlined, StarFilled, StarOutlined, TagsOutlined, UserOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { serverFetch, fileUrl } from '@server';
import { showError, fmtDateTime, fmtDate, fmtMoney } from '@extensions/utils';
import { EmailInput, PhoneInput, formatPhone, isCompletePhone, isValidEmail, normalizeEmail } from '@extensions/contact-mask';
import { useDicts, channelVidName } from '@extensions/dicts';
import TagSelect from '@controls/tag-select/tag-select';
import ResizableDrawer from '@controls/resizable-drawer/resizable-drawer';
import CardList, { CardRow } from '@controls/card-list/card-list';
import Task from '../tasks/task';
import DialogDrawer from '../dialogs/dialog';
import { dialogTitle, formatDialogTime } from '../dialogs/dialog-utils';
import TaskPriority from '../tasks/task-priority';
import UserAvatar from '../tasks/user-avatar';
import Order from '../orders/order';
import { LogoSlot } from './contragent-logo';

const CONTACT_VID = { 1: 'Телефон', 2: 'Email', 3: 'Заметка', 7: 'Адрес' };

// Пустое поле не проверяется. Заполненное — по маске и на уникальность в фирме.
const contactRule = (field, excludeId) => ({
    validator: async (_, value) => {
        const text = field === 'phone' ? formatPhone(value) : normalizeEmail(value);
        if (!text) return;
        if (field === 'phone' && !isCompletePhone(text))
            throw new Error('Формат: +7 (900) 000-00-00');
        if (field === 'email' && !isValidEmail(text))
            throw new Error('Некорректный email');
        let found;
        try {
            found = await serverFetch('contragent/duplicate', {
                queryParams: { [field]: text, excludeId },
            });
        } catch (e) {
            throw new Error(e?.userMessage || e?.message || 'Не удалось проверить уникальность');
        }
        const name = field === 'phone' ? found?.phoneName : found?.emailName;
        if (name) throw new Error(`Уже указан у «${name}»`);
    },
});

// Строка свойства: подпись слева, контрол справа
const Prop = ({ icon, label, children }) => (
    <div className="task-prop">
        <span className="task-prop-label">{icon}{label}</span>
        <div className="task-prop-value">{children}</div>
    </div>
);

// Быстрые ссылки «позвонить / написать» по текущим значениям формы
const QuickLinks = () => {
    const phone = Form.useWatch('phone');
    const email = Form.useWatch('email');
    return (
        <>
            {phone && <a className="ca-link" href={`tel:${phone.replace(/[^\d+]/g, '')}`}><PhoneOutlined /> {phone}</a>}
            {email && <a className="ca-link" href={`mailto:${email}`}><MailOutlined /> {email}</a>}
        </>
    );
};

const HeroIcon = ({ logoFileId }) => {
    const vid = Form.useWatch('vid') ?? 1;
    const name = Form.useWatch('name');
    if (vid === 1 && logoFileId)
        return <img className="ca-hero-logo" src={fileUrl(logoFileId, true)} alt="" />;
    return vid === 1
        ? <span className="ca-hero-icon"><BankOutlined /></span>
        : <UserAvatar name={name || '?'} size={40} />;
};

// Карточка контрагента с вкладками. id=0 — новый контрагент
const Contragent = ({ id, onClose, onSaved, initial }) => {
    const { dicts } = useDicts();
    const [form] = Form.useForm();
    const [item, setItem] = useState(null);
    const [dirty, setDirty] = useState(false);
    const vid = Form.useWatch('vid', form) ?? 1;
    const isNew = id === 0;

    const load = useCallback(() => {
        serverFetch(`contragent/${id}`).then(setItem).catch(showError);
    }, [id]);

    useEffect(() => {
        if (isNew) {
            setItem({ id: 0, vid: 1, tags: [], isDel: false, ...initial });
        } else {
            load();
        }
    }, [id, isNew, load, initial]);

    useEffect(() => {
        if (item) {
            form.setFieldsValue({
                ...item,
                phone: formatPhone(item.phone),
                email: normalizeEmail(item.email),
            });
            // Имя, подставленное из задачи, не считается правкой поля — иначе «ОК» остаётся выключенной
            setDirty(isNew && Boolean(String(item.name ?? '').trim()));
        }
    }, [item, form, isNew]);

    const onSave = () => {
        form.validateFields().then((values) => {
            const body = {
                ...(item ?? {}),
                ...values,
                phone: formatPhone(values.phone),
                email: normalizeEmail(values.email),
            };
            serverFetch(isNew ? 'contragent' : `contragent/${id}`, {
                method: isNew ? 'POST' : 'PUT',
                bodyData: body,
            })
                .then((saved) => {
                    // Метки сохраняются отдельным запросом
                    const tags = values.tags ?? [];
                    return serverFetch(`contragent/${saved.id}/tags`, { method: 'PUT', bodyData: tags })
                        .then(() => { setItem({ ...saved, tags }); onSaved?.({ ...saved, tags }); });
                })
                .then(() => message.success('Сохранено'))
                .catch(showError);
        }).catch((e) => {
            if (e?.errorFields) return;
            showError(e);
        });
    };

    // Ctrl/Cmd+Enter или Ctrl/Cmd+S — сохранить
    const onKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && (e.key === 'Enter' || e.key === 's' || e.key === 'ы')) {
            e.preventDefault();
            if (dirty) form.submit();
        }
    };

    const onLogo = (file) => {
        const name = (file.name || '').toLowerCase();
        if (!['.svg', '.png', '.jpg', '.jpeg'].some((ext) => name.endsWith(ext))) {
            message.error('Допустимы файлы SVG, PNG и JPG');
            return false;
        }
        const fd = new FormData();
        fd.append('file', file);
        serverFetch(`contragent/${item.id}/logo`, { method: 'POST', formData: fd })
            .then((saved) => {
                setItem((prev) => ({ ...saved, tags: prev?.tags ?? saved.tags }));
                message.success('Логотип сохранён');
            })
            .catch(showError);
        return false;
    };

    const onDeleteLogo = () => {
        serverFetch(`contragent/${item.id}/logo`, { method: 'DELETE' })
            .then((saved) => setItem((prev) => ({ ...saved, tags: prev?.tags ?? saved.tags })))
            .catch(showError);
    };

    const userOpts = dicts.users.map((u) => ({
        value: u.id,
        search: u.name,
        label: <span className="task-opt"><UserAvatar name={u.name} size={20} />{u.name}</span>,
    }));

    const mainTab = (
        <Form
            form={form}
            layout="vertical"
            onFinish={onSave}
            onValuesChange={() => setDirty(true)}
            onKeyDown={onKeyDown}
        >
            <div className="task-hero ca-hero">
                <div className="task-hero-top">
                    {vid === 1 && item?.id ? (
                        <Upload
                            showUploadList={false}
                            accept=".svg,.png,.jpg,.jpeg"
                            beforeUpload={onLogo}
                        >
                            <span title={item.logoFileId ? 'Заменить логотип' : 'Загрузить логотип'}>
                                <HeroIcon logoFileId={item.logoFileId} />
                            </span>
                        </Upload>
                    ) : <HeroIcon logoFileId={item?.logoFileId} />}
                    <Form.Item name="name" rules={[{ required: true, message: 'Укажите наименование' }]}>
                        <Input
                            className="task-hero-input"
                            variant="borderless"
                            placeholder={vid === 1 ? 'Название компании' : 'Имя контакта'}
                            autoFocus={isNew}
                        />
                    </Form.Item>
                    <Form.Item name="vid" noStyle>
                        <Segmented options={[{ value: 1, label: 'Компания' }, { value: 0, label: 'Контакт' }]} />
                    </Form.Item>
                </div>
                <div className="task-meta ca-meta">
                    {!isNew && <span className="task-chip">№{item?.id}</span>}
                    {item?.dateReg && <span>с {fmtDate(item.dateReg)}</span>}
                    {vid === 1 && item?.logoFileId && (
                        <Button type="link" size="small" danger onClick={onDeleteLogo}>Удалить логотип</Button>
                    )}
                    <QuickLinks />
                </div>
            </div>

            <div className="task-section task-props">
                <div className="task-props-grid">
                    {vid === 0 && (
                        <Prop icon={<IdcardOutlined />} label="Должность">
                            <Form.Item name="position" noStyle>
                                <Input variant="borderless" placeholder="—" />
                            </Form.Item>
                        </Prop>
                    )}
                    <Prop icon={<PhoneOutlined />} label="Телефон">
                        <Form.Item name="phone" className="ca-prop-field" validateTrigger="onBlur" rules={[contactRule('phone', isNew ? 0 : id)]}>
                            <PhoneInput variant="borderless" placeholder="+7 (___) ___-__-__" />
                        </Form.Item>
                    </Prop>
                    <Prop icon={<MailOutlined />} label="Email">
                        <Form.Item name="email" className="ca-prop-field" validateTrigger="onBlur" rules={[contactRule('email', isNew ? 0 : id)]}>
                            <EmailInput variant="borderless" placeholder="name@company.ru" />
                        </Form.Item>
                    </Prop>
                    <Prop icon={<UserOutlined />} label="Ответственный">
                        <Form.Item name="userId" noStyle>
                            <Select allowClear showSearch variant="borderless" placeholder="—"
                                optionFilterProp="search" options={userOpts} />
                        </Form.Item>
                    </Prop>
                    <Prop icon={<TagsOutlined />} label="Метки">
                        <Form.Item name="tags" noStyle>
                            <TagSelect tags={dicts.tags} vid={1} variant="borderless" placeholder="—" />
                        </Form.Item>
                    </Prop>
                    <Prop icon={<EnvironmentOutlined />} label="Адрес">
                        <Form.Item name="address" noStyle><Input variant="borderless" placeholder="—" /></Form.Item>
                    </Prop>
                    <Prop icon={<IdcardOutlined />} label="Полное имя">
                        <Form.Item name="fullName" noStyle><Input variant="borderless" placeholder="—" /></Form.Item>
                    </Prop>
                </div>
            </div>

            <div className="task-section">
                <div className="task-section-title"><BankOutlined />{vid === 1 ? 'Реквизиты' : 'Документы'}</div>
                {vid === 1 ? (
                    <div className="ca-req">
                        <Form.Item name="inn" label="ИНН"><Input variant="filled" /></Form.Item>
                        <Form.Item name="ogrn" label="ОГРН"><Input variant="filled" /></Form.Item>
                        <Form.Item name="bik" label="БИК"><Input variant="filled" /></Form.Item>
                        <Form.Item name="rs" label="Р/с"><Input variant="filled" /></Form.Item>
                        <Form.Item name="bank" label="Банк" className="ca-req-wide"><Input variant="filled" /></Form.Item>
                    </div>
                ) : (
                    <Form.Item name="passport" label="Паспорт"><Input variant="filled" /></Form.Item>
                )}
            </div>

            <div className="task-section">
                <div className="task-section-title">Комментарий</div>
                <Form.Item name="comment">
                    <Input.TextArea variant="filled" autoSize={{ minRows: 2, maxRows: 10 }} placeholder="Заметки о контрагенте" />
                </Form.Item>
            </div>

            <div className="task-actions">
                <Button type="primary" htmlType="submit" size="large" className="task-ok" disabled={!dirty}>
                    ОК
                    {dirty && <span className="task-dirty-dot" />}
                </Button>
                <Button size="large" onClick={() => onClose?.()}>Закрыть</Button>
                <span className="task-actions-hint">
                    {dirty ? 'Есть несохранённые изменения · Ctrl+Enter — сохранить' : 'Изменений нет'}
                </span>
            </div>
        </Form>
    );

    const disabled = !item || item.id === 0;
    const card = (node) => item && <div className="task-section ca-tab">{node}</div>;

    const tabs = [
        { key: 'main', label: 'Основное', children: mainTab },
        { key: 'contacts', label: 'Информация', disabled, children: card(<ContactsTab contragentId={item?.id} />) },
        { key: 'children', label: 'Контакты', disabled, children: card(<ChildrenTab contragentId={item?.id} />) },
        { key: 'tasks', label: 'Задачи', disabled, children: card(<TasksTab contragentId={item?.id} />) },
        { key: 'dialogs', label: 'Диалоги', disabled, children: card(<DialogsTab contragentId={item?.id} />) },
        { key: 'orders', label: 'Заказы', disabled, children: card(<OrdersTab contragentId={item?.id} />) },
        { key: 'events', label: 'События', disabled, children: card(<EventsTab contragentId={item?.id} />) },
        { key: 'fields', label: 'Доп. поля', disabled, children: card(<FieldsTab contragentId={item?.id} />) },
    ];

    return (
        <ResizableDrawer
            open
            storageKey="contragentDrawerWidth"
            defaultWidth={860}
            title={isNew ? 'Новый контрагент' : `Контрагент №${id}`}
            onClose={() => onClose?.()}
            destroyOnClose
            className="resizable-drawer task-drawer"
        >
            {item && (
                <div className="task-scroll ca-scroll">
                    <Tabs items={tabs} size="small" className="ca-tabs" destroyOnHidden />
                </div>
            )}
        </ResizableDrawer>
    );
};

const CONTACT_ICONS = { 1: <PhoneOutlined />, 2: <MailOutlined />, 3: <FileTextOutlined />, 7: <EnvironmentOutlined /> };
const CONTACT_PLACEHOLDER = { 1: '+7 900 000-00-00', 2: 'name@example.com', 3: 'Текст заметки', 7: 'Город, улица, дом' };
// Ссылка действия для контакта: телефон — позвонить, email — написать
const contactHref = (c) => {
    if (c.vid === 1) return `tel:${c.name.replace(/[^\d+]/g, '')}`;
    if (c.vid === 2) return `mailto:${c.name}`;
    return null;
};

// Редактирование контакта в Drawer. contact.id = 0 — новый
const ContactDrawer = ({ contragentId, contact, onClose, onSaved }) => {
    const [form] = Form.useForm();
    const [dirty, setDirty] = useState(false);
    const isNew = !contact.id;
    const vid = Form.useWatch('vid', form) ?? contact.vid;

    useEffect(() => { form.setFieldsValue(contact); }, [contact, form]);

    const onSave = () => {
        form.validateFields().then((values) => {
            serverFetch(isNew ? `contragent/${contragentId}/contacts` : `contragent/${contragentId}/contacts/${contact.id}`, {
                method: isNew ? 'POST' : 'PUT',
                bodyData: { ...contact, ...values, isPrimary: values.isPrimary ?? false },
            })
                .then(() => { onSaved?.(); onClose?.(); })
                .catch(showError);
        });
    };

    const onDelete = () => {
        serverFetch(`contragent/${contragentId}/contacts/${contact.id}`, { method: 'DELETE' })
            .then(() => { onSaved?.(); onClose?.(); })
            .catch(showError);
    };

    return (
        <ResizableDrawer
            open
            storageKey="contactDrawerWidth"
            defaultWidth={480}
            title="Информация"
            onClose={() => onClose?.()}
            destroyOnClose
            className="resizable-drawer task-drawer"
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={onSave}
                onValuesChange={() => setDirty(true)}
                onKeyDown={(e) => {
                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); form.submit(); }
                }}
            >
                <div className="task-scroll">
                    <div className="task-section ca-contact-form">
                        <Form.Item name="vid" label="Вид">
                            <Segmented block options={Object.entries(CONTACT_VID).map(([v, l]) => ({
                                value: Number(v), label: l, icon: CONTACT_ICONS[v],
                            }))} />
                        </Form.Item>
                        <Form.Item
                            name="name"
                            label="Значение"
                            rules={[
                                { required: true, message: 'Укажите значение' },
                                ...(vid === 2 ? [{ type: 'email', message: 'Некорректный email' }] : []),
                            ]}
                        >
                            {vid === 3 || vid === 7
                                ? <Input.TextArea variant="filled" autoSize={{ minRows: 2, maxRows: 8 }} placeholder={CONTACT_PLACEHOLDER[vid]} autoFocus />
                                : <Input variant="filled" size="large" placeholder={CONTACT_PLACEHOLDER[vid]} autoFocus />}
                        </Form.Item>
                        <Form.Item name="comment" label="Комментарий">
                            <Input variant="filled" placeholder="Например: рабочий, бухгалтерия" />
                        </Form.Item>
                        <Form.Item name="isPrimary" valuePropName="checked">
                            <Checkbox>Основной контакт</Checkbox>
                        </Form.Item>
                    </div>
                    <div className="task-actions">
                        <Button type="primary" htmlType="submit" size="large" className="task-ok" disabled={!dirty}>ОК</Button>
                        <Button size="large" onClick={() => onClose?.()}>Закрыть</Button>
                        {!isNew && (
                            <Popconfirm title="Удалить контакт?" onConfirm={onDelete} okText="Да" cancelText="Нет">
                                <Button size="large" danger type="text" icon={<DeleteOutlined />} style={{ marginLeft: 'auto' }} />
                            </Popconfirm>
                        )}
                    </div>
                </div>
            </Form>
        </ResizableDrawer>
    );
};

const ContactsTab = ({ contragentId }) => {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = useCallback(() => {
        serverFetch(`contragent/${contragentId}/contacts`).then(setItems).catch(showError);
    }, [contragentId]);

    useEffect(() => { load(); }, [load]);

    const onDelete = (contactId) => {
        serverFetch(`contragent/${contragentId}/contacts/${contactId}`, { method: 'DELETE' })
            .then(load).catch(showError);
    };

    const sorted = [...items].sort((a, b) => (a.vid - b.vid) || (Number(b.isPrimary) - Number(a.isPrimary)) || (a.id - b.id));

    return (
        <div>
            <div className="ca-contacts-head">
                <span className="task-section-title" style={{ margin: 0 }}>Информация · {items.length}</span>
                <Button type="primary" icon={<PlusOutlined />}
                    onClick={() => setEditing({ id: 0, vid: 1, name: '', comment: '', isPrimary: false })}>
                    Добавить
                </Button>
            </div>
            {sorted.length === 0 && <div className="task-empty">Информации нет</div>}
            <div className="ca-contacts">
                {sorted.map((c) => {
                    const href = contactHref(c);
                    return (
                        <div key={c.id} className="ca-contact" onClick={() => setEditing(c)}>
                            <span className={`ca-contact-icon v${c.vid}`}>{CONTACT_ICONS[c.vid] ?? <FileTextOutlined />}</span>
                            <div className="ca-contact-main">
                                <div className="ca-contact-value">
                                    {href
                                        ? <a href={href} onClick={(e) => e.stopPropagation()}>{c.name}</a>
                                        : <span>{c.name}</span>}
                                    {c.isPrimary && <Tag color="gold" className="ca-contact-primary">основной</Tag>}
                                </div>
                                <div className="ca-contact-sub">
                                    {CONTACT_VID[c.vid] ?? 'Контакт'}{c.comment ? ` · ${c.comment}` : ''}
                                </div>
                            </div>
                            <span className="ca-contact-tools" onClick={(e) => e.stopPropagation()}>
                                <Tooltip title="Копировать">
                                    <Button size="small" type="text" icon={<CopyOutlined />}
                                        onClick={() => navigator.clipboard?.writeText(c.name).then(() => message.success('Скопировано'))} />
                                </Tooltip>
                                <Popconfirm title="Удалить контакт?" onConfirm={() => onDelete(c.id)} okText="Да" cancelText="Нет">
                                    <Button size="small" type="text" icon={<DeleteOutlined />} />
                                </Popconfirm>
                            </span>
                        </div>
                    );
                })}
            </div>
            {editing && (
                <ContactDrawer
                    contragentId={contragentId}
                    contact={editing}
                    onClose={() => setEditing(null)}
                    onSaved={load}
                />
            )}
        </div>
    );
};

const EventsTab = ({ contragentId }) => {
    const [items, setItems] = useState([]);
    const [name, setName] = useState('');

    const load = useCallback(() => {
        serverFetch(`contragent/${contragentId}/events`).then(setItems).catch(showError);
    }, [contragentId]);

    useEffect(() => { load(); }, [load]);

    const onAdd = () => {
        if (!name.trim()) return;
        serverFetch(`contragent/${contragentId}/events`, { method: 'POST', bodyData: { name } })
            .then(() => { setName(''); load(); })
            .catch(showError);
    };

    const onDelete = (eventId) => {
        serverFetch(`contragent/${contragentId}/events/${eventId}`, { method: 'DELETE' })
            .then(load).catch(showError);
    };

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <Input placeholder="Новое событие" value={name} onChange={(e) => setName(e.target.value)}
                    onPressEnter={onAdd} />
                <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>Добавить</Button>
            </div>
            <CardList
                items={items}
                emptyText="Событий нет"
                onDelete={(r) => onDelete(r.id)}
                renderTitle={(r) => (
                    <>
                        <ClockCircleOutlined />
                        <span className="card-list-muted">{fmtDateTime(r.createdOn)}</span>
                        <span>{r.name}</span>
                    </>
                )}
            />
        </div>
    );
};

const FieldsTab = ({ contragentId }) => {
    const [fields, setFields] = useState([]);
    const [values, setValues] = useState({});

    const load = useCallback(() => {
        Promise.all([
            serverFetch('field?vid=1').catch(() => []),
            serverFetch(`contragent/${contragentId}/fields`).catch(() => []),
        ])
            .then(([fieldsList, objFields]) => {
                setFields(fieldsList);
                const v = {};
                objFields.forEach((f) => { v[f.fieldId] = f.sValue; });
                setValues(v);
            });
    }, [contragentId]);

    useEffect(() => { load(); }, [load]);

    const onSave = () => {
        const body = fields.map((f) => ({ fieldId: f.id, svalue: values[f.id] ?? '' }));
        serverFetch(`contragent/${contragentId}/fields`, { method: 'PUT', bodyData: body })
            .then(() => message.success('Сохранено'))
            .catch(showError);
    };

    return (
        <div>
            {fields.length === 0 && <div style={{ color: '#999' }}>Нет доп. полей (создаются в Настройках)</div>}
            {fields.map((f) => (
                <div key={f.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ width: 200 }}>{f.name}</div>
                    <Input value={values[f.id] ?? ''} style={{ width: 300 }}
                        onChange={(e) => setValues({ ...values, [f.id]: e.target.value })} />
                </div>
            ))}
            {fields.length > 0 && <Button type="primary" onClick={onSave}>Сохранить</Button>}
        </div>
    );
};

const ChildModal = ({ child, onClose, onSaved }) => {
    const [form] = Form.useForm();

    useEffect(() => {
        form.setFieldsValue({
            name: child.name,
            position: child.position,
            phone: formatPhone(child.phone),
            email: normalizeEmail(child.email),
            comment: child.comment,
        });
    }, [child, form]);

    const onSave = () => {
        form.validateFields().then((values) => {
            serverFetch(`contragent/${child.id}`, {
                method: 'PUT',
                bodyData: {
                    ...child,
                    ...values,
                    phone: formatPhone(values.phone),
                    email: normalizeEmail(values.email),
                },
            })
                .then(() => {
                    message.success('Сохранено');
                    onSaved?.();
                    onClose?.();
                })
                .catch(showError);
        }).catch((e) => {
            if (e?.errorFields) return;
            showError(e);
        });
    };

    return (
        <Modal
            open
            title="Контакт"
            onOk={onSave}
            onCancel={onClose}
            okText="Сохранить"
            cancelText="Отмена"
            destroyOnClose
        >
            <Form form={form} layout="vertical">
                <Form.Item name="name" label="Имя" rules={[{ required: true, message: 'Укажите имя' }]}>
                    <Input autoFocus />
                </Form.Item>
                <Form.Item name="position" label="Должность">
                    <Input />
                </Form.Item>
                <Form.Item name="phone" label="Телефон" validateTrigger="onBlur" rules={[contactRule('phone', child.id)]}>
                    <PhoneInput placeholder="+7 (___) ___-__-__" />
                </Form.Item>
                <Form.Item name="email" label="Email" validateTrigger="onBlur" rules={[contactRule('email', child.id)]}>
                    <EmailInput placeholder="name@company.ru" />
                </Form.Item>
                <Form.Item name="comment" label="Комментарий">
                    <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

const ChildrenTab = ({ contragentId }) => {
    const [items, setItems] = useState([]);
    const [free, setFree] = useState([]);
    const [editing, setEditing] = useState(null);
    const [selectedId, setSelectedId] = useState(null);

    const load = useCallback(() => {
        serverFetch(`contragent/${contragentId}/children`).then(setItems).catch(showError);
        serverFetch('contragent', { queryParams: { vid: 0 } }).then(setFree).catch(showError);
    }, [contragentId]);

    useEffect(() => { load(); }, [load]);

    const onAdd = () => {
        if (!selectedId) return;
        serverFetch(`contragent/${contragentId}/children/${selectedId}`, { method: 'POST' })
            .then(() => { setSelectedId(null); load(); })
            .catch(showError);
    };

    const onDelete = (childId) => {
        serverFetch(`contragent/${contragentId}/children/${childId}`, { method: 'DELETE' })
            .then(load).catch(showError);
    };

    const onPrimary = (child) => {
        const request = child.isPrimary
            ? serverFetch(`contragent/${contragentId}/primary`, { method: 'DELETE' })
            : serverFetch(`contragent/${contragentId}/primary/${child.id}`, { method: 'PUT' });
        request.then(setItems).catch(showError);
    };

    const shown = [...items].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || a.name.localeCompare(b.name, 'ru'));

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <Select
                    showSearch
                    allowClear
                    placeholder="Выберите контакт"
                    value={selectedId}
                    onChange={setSelectedId}
                    style={{ flex: 1, maxWidth: 420 }}
                    optionFilterProp="label"
                    options={free.map((c) => ({
                        value: c.id,
                        label: c.phone ? `${c.name} · ${c.phone}` : c.name,
                    }))}
                />
                <Button type="primary" icon={<PlusOutlined />} onClick={onAdd} disabled={!selectedId} />
            </div>
            <CardList
                items={shown}
                emptyText="Контактов нет"
                deleteTitle="Отвязать контакт?"
                onClick={(r) => setEditing(r)}
                onDelete={(r) => onDelete(r.id)}
                renderTitle={(r) => (
                    <>
                        <span onClick={(e) => e.stopPropagation()}>
                            <Tooltip title={r.isPrimary ? 'Снять основной' : 'Сделать основным'}>
                                <Button type="text" size="small"
                                    icon={r.isPrimary
                                        ? <StarFilled style={{ color: '#faad14' }} />
                                        : <StarOutlined />}
                                    onClick={() => onPrimary(r)} />
                            </Tooltip>
                        </span>
                        <UserOutlined />
                        <span>{r.name}</span>
                        {r.position && <span className="card-list-muted">{r.position}</span>}
                        {r.isPrimary && <Tag color="gold">основной</Tag>}
                        <span className="card-list-muted">#{r.id}</span>
                    </>
                )}
                renderBody={(r) => (
                    <>
                        <CardRow icon={<PhoneOutlined />}>{r.phone || r.phone2}</CardRow>
                        <CardRow icon={<MailOutlined />}>{r.email || r.email2}</CardRow>
                        <CardRow className="card-list-muted">{r.comment}</CardRow>
                    </>
                )}
            />
            {editing && (
                <ChildModal child={editing} onClose={() => setEditing(null)} onSaved={load} />
            )}
        </div>
    );
};

const TasksTab = ({ contragentId }) => {
    const { dicts } = useDicts();
    const [items, setItems] = useState([]);
    const [editTask, setEditTask] = useState(null);

    const load = useCallback(() => {
        serverFetch(`contragent/${contragentId}/tasks`).then(setItems).catch(showError);
    }, [contragentId]);

    useEffect(() => { load(); }, [load]);

    const tagById = useMemo(() => new Map(dicts.tags.map((t) => [t.id, t])), [dicts.tags]);
    const statusById = useMemo(() => new Map(dicts.statuses.map((s) => [s.id, s])), [dicts.statuses]);

    const renderTags = (tags) => (tags ?? []).map((tid) => {
        const t = tagById.get(tid);
        return t ? <Tag key={tid} color={t.bgColor || undefined} style={{ color: t.textColor || undefined }}>{t.name}</Tag> : null;
    });

    return (
        <div>
            <Button type="primary" icon={<PlusOutlined />} style={{ marginBottom: 8 }}
                onClick={() => setEditTask(0)}>
                Новая задача
            </Button>
            <CardList
                items={items}
                emptyText="Задач нет"
                onClick={(r) => setEditTask(r.id)}
                rowStyle={(r) => (r.isClosed ? { opacity: 0.6 } : undefined)}
                renderAside={(r) => (
                    <LogoSlot fileId={r.contragentLogoFileId} name={r.contragentLogoName || r.contragentName} empty />
                )}
                renderTitle={(r) => (
                    <>
                        <TaskPriority priority={r.priority} />
                        <span>{r.name}</span>
                        <span className="card-list-muted">#{r.id}</span>
                        {r.statusName && <Tag color={statusById.get(r.statusId)?.color || undefined}>{r.statusName}</Tag>}
                        {r.blockName && <Tag>{r.blockName}</Tag>}
                        {r.versionName && <Tag>v{r.versionName}</Tag>}
                    </>
                )}
                renderBody={(r) => (
                    <>
                        <CardRow icon={<CalendarOutlined />}>{fmtDate(r.docDate)}</CardRow>
                        {r.planDate && <CardRow title="Срок">{`срок ${fmtDate(r.planDate)}`}</CardRow>}
                        <CardRow icon={<UserOutlined />}>{r.userName}</CardRow>
                        <span>{renderTags(r.tags)}</span>
                    </>
                )}
            />
            {editTask !== null && (
                <Task
                    id={editTask}
                    contragentId={contragentId}
                    onClose={() => { setEditTask(null); load(); }}
                />
            )}
        </div>
    );
};

const OrdersTab = ({ contragentId }) => {
    const [items, setItems] = useState([]);
    const [editOrder, setEditOrder] = useState(null);

    const load = useCallback(() => {
        serverFetch(`order?contragentId=${contragentId}`).then(setItems).catch(showError);
    }, [contragentId]);

    useEffect(() => { load(); }, [load]);

    return (
        <div>
            <Button type="primary" icon={<PlusOutlined />} style={{ marginBottom: 8 }}
                onClick={() => setEditOrder(0)}>
                Новый заказ
            </Button>
            <CardList
                items={items}
                emptyText="Заказов нет"
                onClick={(r) => setEditOrder(r.id)}
                renderAside={(r) => (r.contragentLogoFileId || r.contragentLogoName || r.contragentName
                    ? <LogoSlot fileId={r.contragentLogoFileId} name={r.contragentLogoName || r.contragentName} />
                    : null)}
                renderTitle={(r) => (
                    <>
                        {!r.contragentId && <ShoppingCartOutlined />}
                        <span>{r.docNum ? `Заказ №${r.docNum}` : `Заказ #${r.id}`}</span>
                        <span className="card-list-muted">{fmtDate(r.docDate)}</span>
                        {r.statusName && <Tag>{r.statusName}</Tag>}
                        <span className="card-list-sums">
                            <span className="card-list-muted">Сумма </span>{fmtMoney(r.syma)}
                            <span className="card-list-muted" style={{ marginLeft: 12 }}>Оплата </span>{fmtMoney(r.payment)}
                            <span className="card-list-muted" style={{ marginLeft: 12 }}>Долг </span>
                            <span style={Number(r.dolg) > 0 ? { color: 'red' } : undefined}>{fmtMoney(r.dolg)}</span>
                        </span>
                    </>
                )}
                renderBody={(r) => (
                    <>
                        <CardRow icon={<UserOutlined />}>{r.userName}</CardRow>
                        <CardRow className="card-list-muted">{r.comment}</CardRow>
                    </>
                )}
            />
            {editOrder !== null && (
                <Order
                    id={editOrder}
                    contragentId={contragentId}
                    onClose={() => { setEditOrder(null); load(); }}
                    onSaved={load}
                />
            )}
        </div>
    );
};

// Вкладка «Диалоги» контрагента: список диалогов (без архива), клик открывает карточку диалога
const DialogsTab = ({ contragentId }) => {
    const [items, setItems] = useState([]);
    const [editDialog, setEditDialog] = useState(null);

    const load = useCallback(() => {
        serverFetch(`dialog?contragentId=${contragentId}&showHidden=true`).then(setItems).catch(showError);
    }, [contragentId]);

    useEffect(() => { load(); }, [load]);

    const clearState = (id, action, e) => {
        e.preventDefault();
        e.stopPropagation();
        serverFetch(`dialog/${id}/${action}`, { method: 'PUT' })
            .then(() => load())
            .catch(showError);
    };

    return (
        <div>
            <CardList
                items={items}
                emptyText="Диалогов нет"
                onClick={(r) => setEditDialog(r.id)}
                cardProps={(r) => {
                    const names = [];
                    if (r.missed) names.push('dlg-missed');
                    if (r.incoming) names.push('dlg-incoming');
                    if (r.id === editDialog) names.push('dlg-open');
                    return names.length ? { className: names.join(' ') } : {};
                }}
                renderTitle={(r) => (
                    <>
                        {r.channelIconFileId
                            ? <img className="channel-icon" src={fileUrl(r.channelIconFileId, true)} alt="" />
                            : null}
                        <span>{dialogTitle(r)}</span>
                        {r.missed ? (
                            <span onClick={(e) => e.stopPropagation()}>
                                <Tag color="error" closable onClose={(e) => clearState(r.id, 'clear-missed', e)}>
                                    Пропущенный
                                </Tag>
                            </span>
                        ) : null}
                        {r.incoming ? (
                            <span onClick={(e) => e.stopPropagation()}>
                                <Tag color="error" closable onClose={(e) => clearState(r.id, 'clear-incoming', e)}>
                                    Входящее
                                </Tag>
                            </span>
                        ) : null}
                        <Tag>{channelVidName(r.channelVid)}{r.channelName ? ` · ${r.channelName}` : ''}</Tag>
                        {r.line ? <Tag>{formatPhone(r.line)}</Tag> : null}
                        {r.lineHidden ? <Tag>скрытая линия</Tag> : null}
                        <span className="card-list-muted">{formatDialogTime(r.lastUpdated)}</span>
                    </>
                )}
                renderBody={(r) => (
                    <>
                        <CardRow className="card-list-muted card-list-wide">{r.lastMessageText}</CardRow>
                        <CardRow icon={<UserOutlined />}>{r.userName}</CardRow>
                    </>
                )}
            />
            {editDialog !== null && (
                <DialogDrawer
                    id={editDialog}
                    onClose={() => { setEditDialog(null); load(); }}
                    onChanged={load}
                />
            )}
        </div>
    );
};

export default Contragent;
